import "../../../styles/legacy-css/navStyles.css";
import {
  itemDataFromDynamo,
  SearchMatchGroup,
  useUserDataContext,
} from "../../../contexts/UserDataContext";
import { SyntheticEvent, useState } from "react";
import { calculateChildren } from "../../../utils/CalculateItems";
import {
  AddItemButton,
  InUseButton,
  StyledBulkWorkflowButton,
} from "../../../styles/components/Buttons.styled";
import {
  StyledSearchButtonContainer,
  StyledSearchForm,
  StyledSearchInputContainer,
  StyledSearchContainer,
} from "../../../styles/components/Search.styled";
import { Search } from "react-bootstrap-icons";
import { UNTITLED } from "../../../utils/Constants";

// Item search tiers, best match first. Results are grouped by tier (see toMatchGroup), then name.
// 1 - exact: the search term appears in the name/tag as typed
// 2 - spaces removed: "screw driver" matches "screwdriver" and vice versa;
//     or singular: a trailing 's' is dropped from each word ("screws" -> "screw")
// 3 - all words: every word in the term appears ("concrete bit" -> "concrete drill bit"),
//     each word as typed or singular
// 4 - any word: at least one word longer than 3 characters appears (as typed or singular)
const MIN_ANY_WORD_LENGTH = 4;
// Shortest word that gets a trailing 's' dropped, so short words like "gas" are left alone
const MIN_SINGULAR_WORD_LENGTH = 4;

function toMatchGroup(tier: number): SearchMatchGroup {
  if (tier <= 1) return SearchMatchGroup.Exact;
  if (tier <= 3) return SearchMatchGroup.Probable;
  return SearchMatchGroup.Possible;
}

function removeSpaces(value: string) {
  return value.replace(/\s+/g, "");
}

// Drops a trailing 's' from a plural-looking word ("screws" -> "screw", but not "glass")
function toSingular(word: string) {
  if (
    word.length >= MIN_SINGULAR_WORD_LENGTH &&
    word.endsWith("s") &&
    !word.endsWith("ss")
  ) {
    return word.slice(0, -1);
  }
  return word;
}

function getItemSearchKeys(item: itemDataFromDynamo): string[] {
  const keys: string[] = [];
  if (item.Name) keys.push(item.Name.toLowerCase());
  for (const tag of item.Tags ?? []) {
    keys.push(tag.toLowerCase());
  }
  return keys;
}

// Returns the best tier at which a single search term matches the keys, or null for no match
function getTermTier(term: string, keys: string[]): number | null {
  if (keys.some((key) => key.includes(term))) return 1;

  const words = term.split(/\s+/).filter((word) => word !== "");
  const singularTerm = words.map(toSingular).join(" ");
  if (singularTerm !== term && keys.some((key) => key.includes(singularTerm))) {
    return 2;
  }

  const termNoSpaces = removeSpaces(term);
  const singularNoSpaces = removeSpaces(singularTerm);
  if (
    termNoSpaces !== "" &&
    keys.some((key) => {
      const keyNoSpaces = removeSpaces(key);
      return (
        keyNoSpaces.includes(termNoSpaces) ||
        keyNoSpaces.includes(singularNoSpaces)
      );
    })
  ) {
    return 2;
  }

  if (words.length > 1) {
    const wordMatches = (word: string) => {
      const singular = toSingular(word);
      return keys.some((key) => key.includes(word) || key.includes(singular));
    };
    if (words.every(wordMatches)) return 3;
    if (
      words.some(
        (word) => word.length >= MIN_ANY_WORD_LENGTH && wordMatches(word)
      )
    ) {
      return 4;
    }
  }
  return null;
}

// Every AND group must match; an AND group matches at the best tier of its OR terms.
// The item's tier is the worst tier across its AND groups.
function getItemTier(
  item: itemDataFromDynamo,
  itemSearchArray: string[][]
): number | null {
  const keys = getItemSearchKeys(item);
  if (keys.length === 0) return null;
  let itemTier = 0;
  for (const orGroup of itemSearchArray) {
    let groupTier: number | null = null;
    for (const term of orGroup) {
      const termTier = getTermTier(term, keys);
      if (termTier != null && (groupTier == null || termTier < groupTier)) {
        groupTier = termTier;
        if (groupTier === 1) break;
      }
    }
    if (groupTier == null) return null;
    itemTier = Math.max(itemTier, groupTier);
  }
  return itemTier;
}

function compareByName(a: itemDataFromDynamo, b: itemDataFromDynamo) {
  const aName = a.Name ?? "";
  const bName = b.Name ?? "";
  return aName.localeCompare(bName, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function SearchBox() {
  const {
    setCurItemArray,
    searchMap,
    itemMap,
    setSearchActive,
    curParent,
    recalculateCurrentArray,
    parentMap,
    itemSearchValue,
    setItemSearchValue,
    setItemSearchInput,
    locationSearchValue,
    setLocationSearchValue,
    setLocationSearchInput,
    searchActive,
    searchItemsInUse,
    setSearchItemsInUse,
    CancelSearch,
    setItemsInView,
    curItemArray,
    setBulkWorkflow,
    bulkWorkflow,
    setSearchMatchGroups,
  } = useUserDataContext();
  const handleItemSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setItemSearchValue(e.target.value);
  };
  const handleLocationSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    setLocationSearchValue(e.target.value);
  };

  const viewItemsInUse = () => {
    if (!searchItemsInUse) {
      setSearchItemsInUse(true);
      let inUseResultsArray: itemDataFromDynamo[] = [];
      let childrenIds: Set<string> = new Set();
      if (searchActive) {
        //Get all children
        curItemArray.forEach((element) => {
          //Get the children of the element and add to list of childrenIds
          calculateChildren(parentMap, element.ItemId, childrenIds);
          childrenIds.add(element.ItemId);
        });
        childrenIds.forEach((id) => {
          if (itemMap[id].InUse && !inUseResultsArray.includes(itemMap[id])) {
            inUseResultsArray.push(itemMap[id]);
          }
        });
      } else {
        calculateChildren(parentMap, curParent, childrenIds);
        childrenIds.forEach((id) => {
          if (itemMap[id].InUse && !inUseResultsArray.includes(itemMap[id])) {
            inUseResultsArray.push(itemMap[id]);
          }
        });
      }
      if (inUseResultsArray.length > 0) {
        setSearchMatchGroups({});
        setCurItemArray(
          inUseResultsArray.sort((a, b) => {
            const aName = a.Name ?? "";
            const bName = b.Name ?? "";
            return aName.localeCompare(bName, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          })
        );
        setSearchActive(true);
      } else {
        if (searchActive) {
          alert("No Items Currently In Use in the current search results");
        } else {
          alert("No Items Currently In Use in *"+ (itemMap[curParent].Name ?? 'UNTITLED').toUpperCase()+'*');
        }
        setSearchItemsInUse(false);
      }
    } else {
      if (locationSearchValue == "" && itemSearchValue == "") {
        setSearchActive(false);
        recalculateCurrentArray(parentMap, curParent, itemMap, false);
      } else {
        executeSearch(itemSearchValue, locationSearchValue);
      }
      setSearchItemsInUse(false);
    }
  };

  function clearSearch() {
    CancelSearch();
    recalculateCurrentArray(parentMap, curParent, itemMap, false);
  }

  // This allows multiple locations using OR logic
  function parseSimpleORInput(input: string): string[] {
    return input
      .split(/;|\|/)
      .map((term) => term.trim().toLowerCase())
      .filter((term) => term !== "");
  }

  // For items, we want to support OR and AND logic
  function parseSearchInput(input: string): string[][] {
    return input
      .split("&")
      .map((andChunk) =>
        andChunk
          .split(/;|\|/) // OR separators only: `;` or `|`
          .map((term) => term.trim().toLowerCase())
          .filter((term) => term !== "")
      )
      .filter((group) => group.length > 0);
  }

  function executeSearch(itemSearchValue: string, locationSearchValue: string) {
    let itemSearchResultsArray: itemDataFromDynamo[] = [];
    let locationSeachExactMatchResultsArray: itemDataFromDynamo[] = [];
    var itemSearchArray: string[][] = parseSearchInput(itemSearchValue);
    var locationSearchArray: string[] = parseSimpleORInput(locationSearchValue);

    const isSpecialCase =
      itemSearchArray.length === 1 &&
      (itemSearchArray[0][0].toLowerCase() === "l*" ||
        itemSearchArray[0][0].toLowerCase() === "i*" ||
        itemSearchArray[0][0].toLowerCase() === "*" ||
        itemSearchValue === UNTITLED);

    if (locationSearchArray.length > 0 || itemSearchArray.length > 0) {
      setSearchActive(true);
      setItemSearchInput(itemSearchValue);
      setLocationSearchInput(locationSearchValue);
      var matchingLocationIds = new Set<string>();
      var matchingAllLocationIds = new Set<string>();
      // Matching item id -> match group (lower is a better match)
      var matchingItemGroups: Record<string, SearchMatchGroup> = {};
      console.log("searchmap Count - " + Object.keys(searchMap).length);
      // Loop over search map to get matching location ids

      for (const key in searchMap) {
        const keyLower = key.toLowerCase();
        if (
          locationSearchArray.some((searchTerm) =>
            keyLower.includes(searchTerm)
          )
        ) {
          for (const itemId of searchMap[key]) {
            if (itemId in parentMap) {
              matchingLocationIds.add(itemId);
              matchingAllLocationIds.add(itemId);
              calculateChildren(parentMap, itemId).forEach(
                matchingAllLocationIds.add,
                matchingAllLocationIds
              );
            }
          }
        }
      }

      // If it's not a special case calculate items in search, scored by tier
      if (!isSpecialCase && itemSearchArray.length > 0) {
        for (const itemId in itemMap) {
          const item = itemMap[itemId];
          if (item == null) continue;
          const tier = getItemTier(item, itemSearchArray);
          if (tier != null) {
            matchingItemGroups[itemId] = toMatchGroup(tier);
          }
        }
      }
      if (itemSearchArray.length == 0 && matchingLocationIds.size > 0) {
        for (const locationId of matchingLocationIds) {
          console.log("Adding - " + locationId);
          itemSearchResultsArray.push(itemMap[locationId]);
        }
        console.log(itemSearchResultsArray);
      }
      // Only retuen items that are also locations
      else if (itemSearchArray.length == 1 && itemSearchArray[0][0] == "l*") {
        // Return a list of items that follow these rules:
        // If no location search provided then return all location
        // If location search provided return locations that match the location search terms and return none if there are no matching locations
        for (const itemKey in itemMap) {
          if (
            itemMap[itemKey] != null &&
            (matchingAllLocationIds.has(itemMap[itemKey].ParentId) ||
              locationSearchValue == "") &&
            !itemSearchResultsArray.includes(itemMap[itemKey])
          ) {
            if (parentMap[itemKey] != null) {
              itemSearchResultsArray.push(itemMap[itemKey]);
            }
          }
        }
      }
      // Item Only Search
      else if (
        itemSearchArray.length == 1 &&
        itemSearchArray[0][0].toLowerCase() == "i*"
      ) {
        // Return a list of items that follow these rules:
        // If no location search provided then return all items that are not locations
        // If location search provided return non-location items that are in the matching locations. If there are no matching locations then return no results
        for (const itemkey in itemMap) {
          if (
            itemMap[itemkey] != null &&
            (matchingAllLocationIds.has(itemMap[itemkey].ParentId) ||
              locationSearchValue == "") &&
            !itemSearchResultsArray.includes(itemMap[itemkey])
          ) {
            if (parentMap[itemkey] == null) {
              itemSearchResultsArray.push(itemMap[itemkey]);
            }
          }
        }
      }
      // All Item Search
      else if (
        itemSearchArray.length == 1 &&
        itemSearchArray[0][0].toLowerCase() == "*"
      ) {
        // Return a list of items that follow these rules:
        // If no location search provided then return all items
        // If location search provided return items that are in the matching locations. If there are no matching locations then return no results
        for (const key in itemMap) {
          if (
            itemMap[key] != null &&
            (matchingAllLocationIds.has(itemMap[key].ParentId) ||
              locationSearchValue == "") &&
            !itemSearchResultsArray.includes(itemMap[key])
          ) {
            itemSearchResultsArray.push(itemMap[key]);
          }
        }
      }
      // UNTITLED Item Search\
      else if (itemSearchArray.length == 1 && itemSearchValue == UNTITLED) {
        // Return a list of items that follow these rules:
        // If no location search provided then return all UNTITLED (items without names) items
        // If location search provided return UNTITLED items that are in the matching locations. If there are no matching locations then return no results
        for (const key in itemMap) {
          if (
            itemMap[key] != null &&
            (matchingAllLocationIds.has(itemMap[key].ParentId) ||
              locationSearchValue == "") &&
            (itemMap[key].Name == null || itemMap[key].Name == "") &&
            !itemSearchResultsArray.includes(itemMap[key])
          ) {
            itemSearchResultsArray.push(itemMap[key]);
          }
        }
      } else {
        // Return a list of items that follow these rules:
        // If no location search provided then return all the items that matched the item search
        // If location search provided return matched items that are in the matching locations. If there are no matching locations then return no results
        if (locationSearchValue == "") {
          for (const itemId in matchingItemGroups) {
            itemSearchResultsArray.push(itemMap[itemId]);
          }
        } else {
          itemSearchResultsArray = Object.keys(matchingItemGroups)
            .filter((itemId) =>
              matchingAllLocationIds.has(itemMap[itemId].ParentId)
            )
            .map((itemId) => itemMap[itemId]);
        }
      }
      setItemsInView(parseInt(process.env.REACT_APP_ITEM_VIEW_INTERVAL!));
      // Best match group first, then alphabetical. Special-case searches have no
      // groups, so they stay alphabetical.
      const groupOf = (item: itemDataFromDynamo) =>
        matchingItemGroups[item.ItemId] ?? 0;
      setSearchMatchGroups(matchingItemGroups);
      setCurItemArray(
        itemSearchResultsArray.length > 0
          ? itemSearchResultsArray.sort(
              (a, b) => groupOf(a) - groupOf(b) || compareByName(a, b)
            )
          : locationSeachExactMatchResultsArray.sort(compareByName)
      );
      window.scrollTo(0, 0);
    } else {
      clearSearch();
    }
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setSearchItemsInUse(false);
    executeSearch(itemSearchValue, locationSearchValue);
  };

  return (
    <StyledSearchContainer>
      <StyledSearchForm onSubmit={handleSubmit}>
        <StyledSearchInputContainer key="searchContainer">
          <input
            type="text"
            placeholder="Location Search"
            onChange={handleLocationSearchChange}
            value={locationSearchValue}
            data-lpignore="true"
            key="locSearch"
          />
          <input
            type="text"
            placeholder="Item Search"
            onChange={handleItemSearchChange}
            value={itemSearchValue}
            data-lpignore="true"
            key="itemSearch"
          />
        </StyledSearchInputContainer>
        <StyledSearchButtonContainer key="searchButtonContainer">
          <AddItemButton type="submit">
            <Search />
          </AddItemButton>
          {searchActive && (
            <AddItemButton type="button" onClick={clearSearch}>
              Clear
            </AddItemButton>
          )}
        </StyledSearchButtonContainer>
      </StyledSearchForm>
      <InUseButton onClick={viewItemsInUse}>
        In Use? <input checked={searchItemsInUse} type="checkbox" readOnly />
      </InUseButton>
      {searchActive && (
        <StyledBulkWorkflowButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setBulkWorkflow(!bulkWorkflow);
          }}
        >
          Bulk Workflow
        </StyledBulkWorkflowButton>
      )}
    </StyledSearchContainer>
  );
}
